/** @jsx jsx */

import * as React from "react";
import { css, jsx } from "@emotion/react";
import {
  ResponsiveChoropleth,
  ChoroplethBoundFeature,
} from "@nivo/geo";
import { breakpointMax } from "../../../style";
import { addInterval } from "../../functions/interval";
import {
  addArray,
  multiplyArrayByScalar,
  substractArray,
} from "../../functions/array";

const graphLabel = css`
  color: #008c3d;
  text-align: center;
  font-size: 18px;
  font-weight: 400;
  line-height: normal;
  margin-bottom: 2px;
`;
const graphDetails = css`
  color: #657f93;
  text-align: center;
  font-size: 14px;
  font-weight: 400;
  line-height: normal;
`;

const tooltipStyle = css`
  background-color: #ffffff;
  border-radius: 4px;
  padding: 8px;
  border: solid 1px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
`;

const detailsStyle = css`
  color: #657f93;
  text-align: center;
  font-size: 14px;
  font-weight: 400;
  line-height: normal;
`;

const selectWrapperStyle = css`
  position: relative;
  width: 180px;
  margin-bottom: 10px;
  @media (max-width: ${breakpointMax}) {
    padding-left: 16px;
  }
`;

const selectStyle = css`
  margin-bottom: 10px;
  padding: 8px;
  color: hsl(0, 0%, 50%);
  font-size: 15px;
  background-color: white;
  border-color: hsl(0, 0%, 80%);
  border-radius: 4px;
  border-style: solid;
  border-width: 1px;
  width: 180px;
  height: 40px;
  outline: none;
  box-sizing: border-box;
  appearance: none;
  -webkit-transition: all 100ms;
  &:focus {
    box-shadow: 0 0 0 1px #2684ff;
    outline: none;
  }
`;

const DropdownArrow = () => (
  <svg
    height="18"
    width="18"
    viewBox="0 0 20 20"
    aria-hidden="true"
    focusable="false"
    className="dropdown-arrow"
  >
    <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z" fill="#22AA60"></path>
  </svg>
);

// You can find more easing functions at https://easings.net/

function easeInOutCubic(x: number): number {
  return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
}

function getNormalizedTranslation(translation: number, currentZoom: number) {
  return getTranslation(translation, currentZoom, 100);
}

function getFromNormalizedTranslation(
  normalizedTranslation: number,
  targetZoom: number
) {
  return getTranslation(normalizedTranslation, 100, targetZoom);
}

function getTranslation(
  oldTranslation: number,
  oldZoom: number,
  targetZoom: number
) {
  return 0.5 + ((oldTranslation - 0.5) * targetZoom) / oldZoom;
}

function getPositionComputeFunction(
  zoomStart: number,
  zoomEnd: number,
  posStart: [number, number],
  posEnd: [number, number]
) {
  const posStartNormalized = [
    getNormalizedTranslation(posStart[0], zoomStart),
    getNormalizedTranslation(posStart[1], zoomStart),
  ];
  const posEndNormalized = [
    getNormalizedTranslation(posEnd[0], zoomEnd),
    getNormalizedTranslation(posEnd[1], zoomEnd),
  ];
  const gap = substractArray(posEndNormalized, posStartNormalized);
  const zoomGap = zoomEnd - zoomStart;
  const biggestPos =
    zoomStart > zoomEnd
      ? getBiggestPos(zoomStart, posStart, zoomEnd, posEnd)
      : getBiggestPos(zoomEnd, posEnd, zoomStart, posStart);
  const maxStep =
    20 +
    Math.ceil(20 * Math.max(Math.abs(biggestPos[0]), Math.abs(biggestPos[1])));
  return {
    maxStep,
    callback: (
      currentStep: number,
      translationSetter: (translation: [number, number]) => void,
      zoomSetter: (translation: number) => void
    ) => {
      const ratio = easeInOutCubic(currentStep / maxStep);
      const normalizedDestination = addArray(
        posStartNormalized,
        multiplyArrayByScalar(gap, ratio)
      );
      const targetZoom = ratio * zoomGap + zoomStart;
      translationSetter([
        getFromNormalizedTranslation(normalizedDestination[0], targetZoom),
        getFromNormalizedTranslation(normalizedDestination[1], targetZoom),
      ]);
      zoomSetter(targetZoom);
    },
  };
}

function getBiggestPos(
  biggestZoom: number,
  biggestPos: [number, number],
  smallestZoom: number,
  smallestPos: [number, number]
) {
  return substractArray(biggestPos, [
    getTranslation(smallestPos[0], smallestZoom, biggestZoom),
    getTranslation(smallestPos[1], smallestZoom, biggestZoom),
  ]);
}

export const GeoMapDataComponent = ({
  data,
  isRed,
  continentSelected,
}: {
  data: any;
  isRed: boolean;
  continentSelected: any;
}) => {
  const worldcountries = require("./world-countries.json");
  const parsedData = JSON.parse(data);
  const [projectionScale, setProjectionScale] = React.useState(200);
  const [projectionTranslation, setProjectionTranslation] = React.useState<
    [number, number]
  >([0.5, 0.5]);
  const [isMobile, setIsMobile] = React.useState(false);

  const geoMapContainer = css`
    width: 100%;
    position: relative;
    height: 500px;
    margin-bottom: ${!continentSelected ? "70px" : "8px"};

    @media (max-width: ${breakpointMax}) {
      height: 600px;
    }

    @media (max-width: 480px) {
      height: 250px;
    }

    @media (max-width: 320px) {
      height: 200px;
    }
`;

  React.useEffect(() => {
    const updateIsMobile = () => {
      setIsMobile(window.innerWidth < 767);
    };

    updateIsMobile();
    window.addEventListener("resize", updateIsMobile);

    return () => window.removeEventListener("resize", updateIsMobile);
  }, []);

  const continentSettings: {
    [key: string]: { scale: number; translation: [number, number] };
  } = isMobile
      ? {
        "World": { scale: 50, translation: [0.5, 0.5] },
        "Africa": { scale: 140, translation: [0.4, 0.4] },
        "Europe": { scale: 225, translation: [0.5, 1.4] },
        "Asia": { scale: 125, translation: [0.2, 0.8] },
        "North America": { scale: 130, translation: [1.0, 0.8] },
        "South America": { scale: 140, translation: [0.8, 0.1] },
        "Oceania": { scale: 140, translation: [-0.1, 0.2] },
      }
      : {
        "World": { scale: 100, translation: [0.5, 0.5] },
        "Africa": { scale: 300, translation: [0.4, 0.5] },
        "Europe": { scale: 450, translation: [0.5, 1.5] },
        "Asia": { scale: 250, translation: [0.2, 0.8] },
        "North America": { scale: 200, translation: [0.7, 0.8] },
        "South America": { scale: 235, translation: [0.8, 0.2] },
        "Oceania": { scale: 280, translation: [0, 0.2] },
      };

  React.useEffect(() => {
    const updateScale = () => {
      if (window.innerWidth < 330) {
        setProjectionScale(40);
      } else if (window.innerWidth < 480) {
        setProjectionScale(50);
      } else if (window.innerWidth < 768) {
        setProjectionScale(120);
      } else {
        setProjectionScale(100);
      }
    };
    window.addEventListener("resize", updateScale);
    updateScale();
    return () => window.removeEventListener("resize", updateScale);
  }, []);

  const countryNameMap = React.useMemo(
    () =>
      parsedData.reduce(
        (
          acc: { [key: string]: { name: string; details: any; url: string } },
          country: any
        ) => {
          acc[country.id] = {
            name: country.name,
            details: country.details,
            url: country.url,
          };
          return acc;
        },
        {}
      ),
    [parsedData]
  );

  const choroplethData = React.useMemo(
    () =>
      parsedData.map((country: any) => ({
        id: country.id,
        value: country.details,
        name: countryNameMap[country.id]?.name || "Not Found",
      })),
    [parsedData, countryNameMap]
  );

  const getCountryDetails = React.useCallback(
    (id: string) => {
      const details = countryNameMap[id]?.details || {};
      const detailElements = [];
      const url = countryNameMap[id]?.url?.url;

      for (const [key, value] of Object.entries(details)) {
        detailElements.push(<div key={key}>{value}</div>);
      }

      return <div css={detailsStyle}>{detailElements}</div>;
    },
    [countryNameMap]
  );

  const handleCountryClick = React.useCallback(
    (feature: ChoroplethBoundFeature & { id: string; value: number }) => {
      const countryId = feature.id;
      const url = countryNameMap[countryId]?.url?.url;
      if (url) {
        const fullUrl = url.startsWith("http") ? url : `https://${url}`;
        window.location.href = fullUrl;
      } else {
        console.log(feature);
      }
    },
    [countryNameMap]
  );

  const moveAndZoomIn = React.useCallback(
    (selectedContinent: string) => {
      const computedFunction = getPositionComputeFunction(
        projectionScale,
        continentSettings[selectedContinent].scale,
        projectionTranslation,
        continentSettings[selectedContinent].translation
      );
      addInterval(25, computedFunction.maxStep, (currentStep: number) =>
        computedFunction.callback(
          currentStep,
          setProjectionTranslation,
          setProjectionScale
        )
      );
    },
    [projectionTranslation, projectionScale, continentSettings]
  );

  const handleContinentChange = React.useCallback(
    (selectedContinent: string) => {
      const scaleDiff =
        continentSettings[selectedContinent].scale - projectionScale;
      if (true || (scaleDiff > 20 && scaleDiff < -20)) {
        moveAndZoomIn(selectedContinent);
      }
    },
    [moveAndZoomIn, continentSettings, projectionScale]
  );

  const [initialZoomDone, setInitialZoomDone] = React.useState(false);

  React.useEffect(() => {
    if (continentSelected) {
      const timeoutId = setTimeout(() => {
        handleContinentChange(continentSelected as string);
        setInitialZoomDone(true);
      }, 300);
  
      return () => clearTimeout(timeoutId);
    }
  }, [continentSelected, handleContinentChange, initialZoomDone]);

  return (
    <section css={geoMapContainer}>
      {!continentSelected && (
        <div css={selectWrapperStyle}>
          <select 
          onChange={(event) => handleContinentChange(event.target.value)}
          css={selectStyle}>
            {Object.keys(continentSettings).map((continent) => (
              <option key={continent} value={continent}>
                {continent}
              </option>
            ))}
          </select>
          <div
            style={{
              position: "absolute",
              right: "10px",
              top: "45%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
            }}
          >
            <DropdownArrow />
          </div>
        </div>
      )}
      {data && !!data?.length ? (
        <ResponsiveChoropleth
          data={choroplethData}
          domain={[0, 99999]}
          projectionType="mercator"
          features={worldcountries.features}
          projectionScale={projectionScale}
          projectionTranslation={projectionTranslation}
          projectionRotation={[0, 0, 0]}
          margin={{ top: 40, right: 20, bottom: 0, left: 0 }}
          colors={[isRed ? "#CA1234" : "#008C3D"]}
          unknownColor="#a9c3cb"
          tooltip={({
            feature,
          }: {
            feature: ChoroplethBoundFeature & { id: string; value: number };
          }): React.ReactElement => (
            <div css={countryNameMap[feature.id]?.name ? tooltipStyle : null}>
              <div css={graphLabel}>{countryNameMap[feature.id]?.name}</div>
              <div css={graphDetails}>{getCountryDetails(feature.id)}</div>
            </div>
          )}
          onClick={handleCountryClick}
        />
      ) : (
        <p>Empty map data</p>
      )}
    </section>
  );
};
